import React, { useId, useState } from 'react'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../shared/error/server-message'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { aiToolsAvailable, markOf, putMarks } from './ai-tools-api'
import { plural } from '../../../../shared/sso-workspace/format'
import { CONSUMER_HELP, MARK_STATUSES, NOTE_MAX, consumerProblem, kindItems, noteProblem } from './ai-tools-words'
import '../../org/api-keys/api-keys.scss'

// Change one processing mark (contract §4.5, R20): for which consumer (one
// that has marked before, or a new name), its status, and an optional note.
// Unprocessed removes that consumer's mark, so its next export picks the
// record up again. PUT /processing names the targets that no longer exist
// (`missing`): then nothing was marked, and the dialog says so and stays.
// Callers offer it only with SYS_AI_TOOLS_TELEMETRY update.
//
// target: { kind: 'session' | 'event' | 'postmortem' | 'finding', id, label }
// for one row, or { kind, ids, label } for rows selected one by one (select
// mode): PUT /processing takes at most 500 marks, so they are sent 500 at a
// time; a call that fails after earlier ones were saved ends the dialog with
// how many were (onMarked's `partial`), so the list says it.

// The select's "Another consumer…" value: it starts with "_", which no
// consumer can (CONSUMER_RULE), so it never collides with a real one.
export const NEW_CONSUMER = '__new__'
const KIND_WORDS = { session: 'session', event: 'record', postmortem: 'postmortem', finding: 'finding' }
export const MARKS_PER_CALL = 500
const chunks = (list, size) => Array.from({ length: Math.ceil(list.length / size) }, (_, i) => list.slice(i * size, (i + 1) * size))

// The fields both mark dialogs share (this one and "Mark all matching…"):
// which consumer (a known one, or a new name), its status, and a note.
// useMarkFields(consumers) -> fields; <MarkFields fields onEdit /> draws them
// (onEdit: a field was touched, so the dialog may clear its error line).
// fields.problem() is the first thing wrong with them, or null.
export const useMarkFields = consumers => {
  const known = Array.isArray(consumers) ? consumers : []
  const [choice, setChoice] = useState(known.length ? known[0] : NEW_CONSUMER)
  const [typed, setTyped] = useState('')
  const [status, setStatus] = useState('processed')
  const [note, setNote] = useState('')
  const consumer = choice === NEW_CONSUMER ? typed.trim() : choice
  const clearing = status === 'unprocessed'
  // markOf sends the note trimmed, so the limit is held to the trimmed note.
  const problem = () => consumerProblem(consumer) || (clearing ? null : noteProblem(note.trim()))
  return { known, choice, setChoice, typed, setTyped, status, setStatus, note, setNote, consumer, clearing, problem }
}

export const MarkFields = ({ fields, onEdit = () => {} }) => {
  const ids = { consumer: useId(), name: useId(), status: useId(), note: useId() }
  const { known, choice, typed, status, note, clearing } = fields
  const help = <div className="sw-hint small">{CONSUMER_HELP}</div>
  return (
    <>
      {known.length > 0 &&
        <div className="mb-3">
          <label className="sw-label" htmlFor={ids.consumer}>Processed by</label>
          <select id={ids.consumer} className="form-select" value={choice} onChange={e => { onEdit(); fields.setChoice(e.target.value) }}>
            {known.map(name => <option key={name} value={name}>{name}</option>)}
            <option value={NEW_CONSUMER}>A new name</option>
          </select>
          {help}
        </div>}
      {choice === NEW_CONSUMER &&
        <div className="mb-3">
          <label className="sw-label" htmlFor={ids.name}>New name</label>
          <input id={ids.name} className="form-control sw-mono" type="text" value={typed} placeholder="triage" autoComplete="off" autoCapitalize="none" spellCheck={false}
            onChange={e => { onEdit(); fields.setTyped(e.target.value) }} />
          {!known.length && help}
        </div>}
      <div className="mb-3">
        <div className="sw-label" id={ids.status}>Status</div>
        <div className="ak-choices ak-choices-inline" role="radiogroup" aria-labelledby={ids.status}>
          {MARK_STATUSES.map(option =>
            <label key={option.value}>
              <input type="radio" name={ids.status} checked={status === option.value} onChange={() => { onEdit(); fields.setStatus(option.value) }} />
              {option.label}
            </label>)}
        </div>
      </div>
      <div className="mb-3">
        <label className="sw-label" htmlFor={ids.note}>Note (optional)</label>
        <textarea id={ids.note} className="form-control" rows={2} maxLength={NOTE_MAX + 100} value={note} disabled={clearing} onChange={e => { onEdit(); fields.setNote(e.target.value) }} />
      </div>
    </>
  )
}

export const MarkDialog = ({ target, consumers, onCancel, onMarked }) => {
  const fields = useMarkFields(consumers)
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const many = Array.isArray(target.ids)
  const words = many ? kindItems(target.kind) : KIND_WORDS[target.kind] || 'record'
  const { consumer, status, note } = fields

  const saveMany = async () => {
    const ids = target.ids
    const total = { marked: 0, cleared: 0, missing: [] }
    let done = 0
    for (const part of chunks(ids, MARKS_PER_CALL)) {
      let answer
      try {
        answer = await putMarks(part.map(id => markOf({ consumer, kind: target.kind, id, status, note })))
      } catch (err) {
        setBusy(false)
        if (!done) return setError(serverMessage(err, "The marks couldn't be saved."), err)
        return onMarked({ ...total, partial: { done, of: ids.length, error: serverMessage(err, "The rest couldn't be saved."), failure: err } })
      }
      done += part.length
      const a = answer || {}
      total.marked += a.marked || 0
      total.cleared += a.cleared || 0
      total.missing.push(...(Array.isArray(a.missing) ? a.missing : []))
    }
    setBusy(false)
    if (total.missing.length >= ids.length) return setError(`These ${words} no longer exist, so nothing was marked.`)
    onMarked(total)
  }

  const save = async () => {
    if (busy) return
    if (!aiToolsAvailable()) return setError("AI tools telemetry isn't available in this build, so nothing was marked.")
    const problem = fields.problem()
    if (problem) return setError(problem)
    setBusy(true)
    setError(null)
    if (many) return saveMany()
    let answer
    try {
      answer = await putMarks([markOf({ consumer, kind: target.kind, id: target.id, status, note })])
    } catch (err) {
      setBusy(false)
      setError(serverMessage(err, "The mark couldn't be saved."), err)
      return
    }
    setBusy(false)
    if (answer && Array.isArray(answer.missing) && answer.missing.length) return setError(`This ${words} no longer exists, so nothing was marked.`)
    // Outside the try: the mark is saved, so what the parent does next can
    // never read as "The mark couldn't be saved."
    onMarked(answer || {})
  }

  return (
    <ConfirmDialog
      open
      title={`Mark ${target.label}`}
      body={many
        ? `Records who processed these ${words}, and how. Clear the mark takes that name's mark off, so its next download includes them again.`
        : `Records who processed this ${words}, and how. Clear the mark takes that name's mark off, so its next download includes it again.`}
      confirmText="Save mark"
      confirmClassName="btn sw-btn-primary"
      busy={busy}
      error={error}
      failure={failure}
      onConfirm={save}
      onCancel={onCancel}>
      <MarkFields fields={fields} onEdit={() => setError(null)} />
    </ConfirmDialog>
  )
}

// What marks said, for the list's status line: "Marked 12 sessions.",
// "Cleared 3 marks.", with the rows that were gone, and how far a write that
// failed part way got ("Marked 500 of 1,200 sessions. The rest couldn't be
// saved: ...").
export const markedText = (kind, answer) => {
  const a = answer || {}
  const items = kindItems(kind)
  const noun = KIND_WORDS[kind] || 'record'
  const gone = Array.isArray(a.missing) && a.missing.length ? ` ${plural(a.missing.length, noun)} no longer ${a.missing.length === 1 ? 'exists' : 'exist'}.` : ''
  if (a.partial) return `Marked ${a.partial.done.toLocaleString()} of ${plural(a.partial.of, noun)}. ${a.partial.error}${gone}`
  const said = a.cleared && !a.marked ? `Cleared ${plural(a.cleared, 'mark')}.` : `Marked ${plural(a.marked || 0, noun, items)}.`
  return `${said}${gone}`
}
